import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { Personne } from "models";
import { useNavigate } from "react-router";
import {
  useErrorHandler,
  useSnackbarErrorHandler,
  withPageErrorBoundary,
} from "utils/errorHandling";
import { elusService } from "services";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { routesConfig } from "config/app-config";
import ToastMessages from "constants/ToastMessages";
import { enqueueSnackbar } from "notistack";
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { Authorization } from "@sdeapps/react-core";
import PageTitle from "components/PageTitle";
import { ApplicationRole } from "constants/ApplicationRole";
import InformationBox from "components/InformationBox";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import { ControlledPersonneAutoComplete } from "components/inputs/ControlledPersonneAutoComplete";
import { elusUtil } from "utils";
import { nbsp } from "constants/InputRules";

interface PersonnesFusionFormData {
  personneAGarder: Personne | null;
  personneAFusionner: Personne | null;
}

function FusionPersonnesPage(): ReactNode {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { catchErrors, isLoading } = useErrorHandler();
  // useSnackbarErrorHandler permet de préciser à l'utilisateur la faute dans la requête qui n'aurait pas permis de fusionner les personnes
  const { catchErrors: catchFusionErrors, isLoading: isSending } = useSnackbarErrorHandler();

  const [personnes, setPersonnes] = useState<Array<Personne>>([]);
  useEffect(() => {
    async function getPersonnes(): Promise<void> {
      const _personnes = await elusService.getAllPersonnes();
      setPersonnes(_personnes);
    }
    void catchErrors(getPersonnes);
  }, [catchErrors]);

  const formMethods = useForm<PersonnesFusionFormData>({
    shouldFocusError: false,
    defaultValues: { personneAGarder: null, personneAFusionner: null },
    mode: "onChange",
  });
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = formMethods;
  const { personneAGarder, personneAFusionner } = useWatch<PersonnesFusionFormData>({
    control,
  });

  const sendData: SubmitHandler<PersonnesFusionFormData> = async function ({
    personneAGarder,
    personneAFusionner,
  }: PersonnesFusionFormData): Promise<void> {
    if (personneAGarder == null || personneAFusionner == null) return;
    if (personneAGarder.id === personneAFusionner.id) {
      enqueueSnackbar({
        variant: "error",
        message: ToastMessages.ERROR_FUSION_SAME_PERSONNE,
      });
      return;
    }

    await catchFusionErrors(async () => {
      await elusService.fusionnerPersonnes(personneAGarder.id, personneAFusionner.id);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.FUSION_PERSONNES_SUCCESS,
      });
      navigate(routesConfig.admin.path);
    });
  };

  return (
    <Container>
      <Authorization roles={ApplicationRole.ADMINISTRATOR}>
        <FormProvider {...formMethods}>
          <Grid container spacing={2} size={12}>
            <PageTitle title="Fusion de personnes" />
            <Typography>
              Fusionner une personne "à garder" avec une autre personne "à fusionner" revient à
              garder toutes les informations de la personne "à garder", c'est-à-dire{nbsp}:
              <ul>
                <li>son nom</li>
                <li>son prénom</li>
                <li>sa date de naissance</li>
                <li>son sexe</li>
                <li>ses coordonnées privées</li>
                <li>ses fonctions SDEA</li>
                <li>ses mandats institutionnels</li>
              </ul>
              Si la personne "à garder" n'a pas de coordonnées privées, celles de la personne "à
              fusionner" lui sont copiées.
              <br />
              Toutes les fonctions SDEA et les mandats institutionnels de la personne "à fusionner"
              sont réaffectés à la personne "à garder". Enfin, la personne "à fusionner" est
              supprimée.
              <br />
              Cette opération est <strong>irréversible</strong>.
            </Typography>
            <InformationBox icon={MergeTypeIcon} articleTitle="Sélection des personnes à fusionner">
              <Grid container size={{ xs: 12, md: 6 }}>
                <Grid size={12}>
                  <Typography variant="subtitle2">1. Choisir la personne à garder</Typography>
                </Grid>
                <Grid size={12}>
                  <ControlledPersonneAutoComplete
                    name="personneAGarder"
                    label="Personne à garder *"
                    personnes={personnes}
                    isLoading={isLoading}
                    rules={{ required: "Ce champ est obligatoire" }}
                  />
                </Grid>
              </Grid>

              <Grid container size={{ xs: 12, md: 6 }}>
                <Grid size={12}>
                  <Typography variant="subtitle2">
                    2. Choisir la personne à fusionner{" "}
                    <Typography variant="caption">(sera supprimée)</Typography>
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <ControlledPersonneAutoComplete
                    name="personneAFusionner"
                    label="Personne à fusionner *"
                    personnes={personnes}
                    isLoading={isLoading}
                    rules={{ required: "Ce champ est obligatoire" }}
                  />
                </Grid>
              </Grid>
            </InformationBox>

            <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
              <Button href={routesConfig.admin.path} color="error">
                Annuler
              </Button>
              <Button
                variant="contained"
                loadingPosition="start"
                type="button"
                onClick={() => {
                  setIsDialogOpen(true);
                }}
                disabled={!isValid}
                startIcon={<MergeTypeIcon />}>
                Fusionner les personnes
              </Button>
            </Grid>
          </Grid>
        </FormProvider>
        {personneAGarder != null && personneAFusionner != null && (
          <Dialog
            open={isDialogOpen}
            onClose={() => {
              setIsDialogOpen(false);
            }}>
            <DialogTitle>Confirmation requise</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Êtes-vous sûr(e) de vouloir fusionner les personnes suivantes{nbsp}?
                <br />
                Cette opération est <strong>irréversible</strong>.
                <br />
                <br />
              </DialogContentText>
              <DialogContentText>
                Personne à garder :<br />
                <Link
                  target="_blank"
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  href={routesConfig.elu.getParameterPath(personneAGarder.id!)}>
                  {elusUtil.getPersonneString(personneAGarder as Personne)}
                </Link>
                <br />
                <br />
                Personne à fusionner (sera supprimée) :<br />
                <Link
                  target="_blank"
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  href={routesConfig.elu.getParameterPath(personneAFusionner.id!)}>
                  {elusUtil.getPersonneString(personneAFusionner as Personne)}
                </Link>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setIsDialogOpen(false);
                }}
                color="error"
                disabled={isSending}>
                Annuler
              </Button>
              <Button
                variant="contained"
                type="button"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={handleSubmit(sendData)}
                startIcon={<MergeTypeIcon />}
                loading={isSending}>
                Oui, fusionner les personnes
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Authorization>
    </Container>
  );
}

export const FusionPersonnesPagesWithErrorBoundary = withPageErrorBoundary(FusionPersonnesPage);
